import { Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import type { FinancialTransactionItem } from "../../../../../types/project-finance.types.tsx"

interface Props {
  record: FinancialTransactionItem
  compact?: boolean
}

export const PaymentsSummary: React.FC<Props> = ({ record, compact }) => {
  const { t } = useTranslation()
  const planned = parseFloat(record.value)
  const paid = parseFloat(record.exercised_value ?? "0")

  if (compact) {
    const compactText = (() => {
      if (paid < 0)
        return t("finance.payments.netRefund", { amount: budgetFormat(-paid) })
      if (paid === 0) return t("finance.payments.noPayments")
      if (record.is_overpaid)
        return t("finance.tooltips.overpaidBy", {
          amount: budgetFormat(paid - planned)
        })
      if (paid >= planned)
        return t("finance.payments.fullyPaid", {
          amount: budgetFormat(planned)
        })
      return t("finance.payments.paidOf", {
        paid: budgetFormat(paid),
        planned: budgetFormat(planned)
      })
    })()

    if (record.is_overpaid) {
      return (
        <Typography.Text className="text-xs whitespace-normal text-red-500 font-semibold">
          {compactText}
        </Typography.Text>
      )
    }

    return (
      <Typography.Text type="secondary" className="text-xs whitespace-normal">
        {compactText}
      </Typography.Text>
    )
  }

  if (paid < 0) {
    return (
      <Typography.Text>
        <Tag color="volcano">
          {t("finance.payments.netRefund", { amount: budgetFormat(-paid) })}
        </Tag>{" "}
        {t("finance.payments.plannedSuffix", { amount: budgetFormat(planned) })}
      </Typography.Text>
    )
  }
  if (paid === 0) {
    return (
      <Typography.Text>
        {t("finance.payments.noPaymentsWithPlan", {
          amount: budgetFormat(planned)
        })}
      </Typography.Text>
    )
  }
  if (record.is_overpaid) {
    return (
      <Tag color="orange">
        {t("finance.tooltips.overpaidBy", {
          amount: budgetFormat(paid - planned)
        })}
      </Tag>
    )
  }
  if (paid >= planned) {
    return (
      <Tag color="green">
        {t("finance.payments.fullyPaid", { amount: budgetFormat(planned) })}
      </Tag>
    )
  }
  const remaining = planned - paid
  return (
    <Typography.Text>
      {t("finance.payments.paidOfRemaining", {
        paid: budgetFormat(paid),
        planned: budgetFormat(planned),
        remaining: budgetFormat(remaining)
      })}
    </Typography.Text>
  )
}
